export type DealerPhotoReorderedItem = {
  url: string
  fromIndex: number
  toIndex: number
}

export type DealerPhotoDiff = {
  before: string[]
  after: string[]
  added: string[]
  removed: string[]
  reordered: DealerPhotoReorderedItem[]
}

export type DealerPhotoDraftEditRow = {
  fieldKey: 'dealer_photos'
  beforeValue: string[]
  afterValue: string[]
}

function normalizePhotoUrls(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
}

export function buildDealerPhotoDraftEditRow(
  liveValue: unknown,
  draftValue: unknown,
): DealerPhotoDraftEditRow | null {
  const beforeValue = normalizePhotoUrls(liveValue)
  const afterValue = normalizePhotoUrls(draftValue)

  if (JSON.stringify(beforeValue) === JSON.stringify(afterValue)) {
    return null
  }

  return {
    fieldKey: 'dealer_photos',
    beforeValue,
    afterValue,
  }
}

function buildOccurrenceEntries(urls: string[]) {
  const counts = new Map<string, number>()
  return urls.map((url, index) => {
    const occurrence = (counts.get(url) || 0) + 1
    counts.set(url, occurrence)
    return {
      key: `${url}__${occurrence}`,
      url,
      index,
    }
  })
}

export function buildDealerPhotoDiff(beforeValue: unknown, afterValue: unknown): DealerPhotoDiff {
  const before = normalizePhotoUrls(beforeValue)
  const after = normalizePhotoUrls(afterValue)

  const beforeEntries = buildOccurrenceEntries(before)
  const afterEntries = buildOccurrenceEntries(after)
  const beforeIndexByKey = new Map(beforeEntries.map((entry) => [entry.key, entry.index]))
  const afterIndexByKey = new Map(afterEntries.map((entry) => [entry.key, entry.index]))

  const added = afterEntries.filter((entry) => !beforeIndexByKey.has(entry.key)).map((entry) => entry.url)
  const removed = beforeEntries.filter((entry) => !afterIndexByKey.has(entry.key)).map((entry) => entry.url)
  const sharedBeforeEntries = beforeEntries.filter((entry) => afterIndexByKey.has(entry.key))
  const sharedAfterEntries = afterEntries.filter((entry) => beforeIndexByKey.has(entry.key))
  const sharedBeforePositionByKey = new Map(sharedBeforeEntries.map((entry, index) => [entry.key, index]))
  const reordered = sharedAfterEntries
    .map((entry, index) => ({
      url: entry.url,
      fromIndex: beforeIndexByKey.get(entry.key) ?? entry.index,
      toIndex: entry.index,
      fromSharedIndex: sharedBeforePositionByKey.get(entry.key) ?? index,
      toSharedIndex: index,
    }))
    .filter((entry) => entry.fromSharedIndex !== entry.toSharedIndex)
    .map(({ url, fromIndex, toIndex }) => ({
      url,
      fromIndex,
      toIndex,
    }))

  return {
    before,
    after,
    added,
    removed,
    reordered,
  }
}

export function hasDealerPhotoVisualChanges(diff: DealerPhotoDiff): boolean {
  return diff.added.length > 0 || diff.removed.length > 0 || diff.reordered.length > 0
}
